import { z } from 'zod'

/**
 * Auth roles. Match the backend's enum exactly. If you add a role here,
 * also add it in `server/app/models/user.py`.
 *
 * Privilege order (highest to lowest):
 *   THINKADMIN        — internal: cross-tenant god mode
 *   SUPER_ADMIN       — tenant owner / admin
 *   OPERATION_MANAGER — facility manager (mirrored from Voice); same access as NURSE
 *   SALES_DIRECTOR    — Voice role; in Vitals treated as VIEWER-level
 *   FRONT_DESK        — Voice role; in Vitals treated as VIEWER-level
 *   NURSE             — records vitals, edits residents
 *   VIEWER            — read-only
 */
export const roleSchema = z.enum(['THINKADMIN', 'SUPER_ADMIN', 'OPERATION_MANAGER', 'SALES_DIRECTOR', 'FRONT_DESK', 'NURSE', 'VIEWER'])
export type Role = z.infer<typeof roleSchema>

export const authUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: roleSchema,
  tenant_id: z.string(),
  facility_id: z.string().nullable(),
  tenant_slug: z.string().nullable().optional(),
})
export type AuthUser = z.infer<typeof authUserSchema>

export const ROLE_LABELS: Record<Role, string> = {
  THINKADMIN: 'Think Admin',
  SUPER_ADMIN: 'Admin',
  OPERATION_MANAGER: 'Operation Manager',
  SALES_DIRECTOR: 'Sales Director',
  FRONT_DESK: 'Front Desk',
  NURSE: 'Nurse',
  VIEWER: 'Viewer',
}

/**
 * Default landing route for a role. THINKADMIN is cross-tenant with no
 * facility, so the tenant-scoped residents dashboard is empty/meaningless
 * for them — send them to client management instead. Everyone else lands
 * on the residents/vitals dashboard.
 */
export function homePathForRole(role: Role): string {
  return role === 'THINKADMIN' ? '/admin/organizations' : '/residents'
}

/**
 * Returns true if the given path is accessible to the role. Used after login
 * to validate that a preserved `from` path isn't restricted to a different role.
 */
export function canAccessPath(role: Role, path: string): boolean {
  if (path.startsWith('/admin')) return role === 'THINKADMIN'
  if (path === '/team' || path === '/facilities') return role === 'SUPER_ADMIN' || role === 'THINKADMIN'
  return true // /, /residents, /login, etc. are open to any authenticated user
}
